import React,{useState,useRef,useCallback,useEffect} from 'react';
import {SUPA_URL,VAPID_PUBLIC_KEY,api} from '../lib/api';

export function urlBase64ToUint8Array(base64String){
  const padding="=".repeat((4-base64String.length%4)%4);
  const base64=(base64String+padding).replace(/-/g,"+").replace(/_/g,"/");
  const rawData=atob(base64);
  return Uint8Array.from([...rawData].map(c=>c.charCodeAt(0)));
}

export async function subscribeToPush(token,userId){
  if(!("serviceWorker" in navigator)||!("PushManager" in window)){
    alert("Push notifications aren't supported in this browser.");
    return false;
  }
  if(VAPID_PUBLIC_KEY==="REPLACE_WITH_YOUR_VAPID_PUBLIC_KEY"){
    alert("Push notifications aren't set up yet — the app owner needs to add a VAPID key first.");
    return false;
  }
  try{
    const permission=await Notification.requestPermission();
    if(permission!=="granted"){alert("Notification permission was not granted.");return false;}
    const reg=await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    let sub=await reg.pushManager.getSubscription();
    if(!sub){
      sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:urlBase64ToUint8Array(VAPID_PUBLIC_KEY)});
    }
    const json=sub.toJSON();
    await api.post(token,"push_subscriptions",[{user_id:userId,endpoint:json.endpoint,p256dh:json.keys.p256dh,auth_key:json.keys.auth}]);
    return true;
  }catch(e){
    console.error("Push subscription failed:",e);
    alert("Couldn't enable push notifications — check the browser console for details.");
    return false;
  }
}

export async function unsubscribeFromPush(token){
  try{
    if(!("serviceWorker" in navigator))return;
    const reg=await navigator.serviceWorker.getRegistration();
    const sub=await reg?.pushManager.getSubscription();
    if(sub){
      const endpoint=sub.endpoint;
      await sub.unsubscribe();
      await fetch(`${SUPA_URL}/rest/v1/push_subscriptions?endpoint=eq.${encodeURIComponent(endpoint)}`,{method:"DELETE",headers:api.h(token)});
    }
  }catch(e){ console.error("Unsubscribe failed:",e); }
}

export async function sendPushNotification(token,targetUserIds,category,title,body,url){
  if(!targetUserIds||targetUserIds.length===0)return{ok:false,reason:"no_recipients"};
  // Fires both channels in parallel — push and email each filter their own
  // recipients server-side based on notification_preferences, so it's safe
  // to always call both from every call site rather than threading a
  // per-call "which channels" flag through the whole app.
  const [pushResult]=await Promise.all([
    sendPushOnly(token,targetUserIds,category,title,body,url),
    sendEmailNotification(token,targetUserIds,category,title,body,url),
  ]);
  return pushResult;
}

async function sendPushOnly(token,targetUserIds,category,title,body,url){
  try{
    const r=await fetch(`${SUPA_URL}/functions/v1/send-push-notification`,{
      method:"POST",
      headers:{...api.h(token),"Content-Type":"application/json"},
      body:JSON.stringify({targetUserIds,category,title,body,url}),
    });
    if(!r.ok){
      let detail="";
      try{detail=await r.text();}catch(e){}
      console.error(`sendPushNotification failed (${r.status}):`,detail);
      return{ok:false,reason:"http_error",status:r.status};
    }
    return{ok:true,count:targetUserIds.length};
  }catch(e){ console.error("sendPushNotification failed:",e); return{ok:false,reason:"network_error"}; }
}

export async function sendEmailNotification(token,targetUserIds,category,title,body,url){
  if(!targetUserIds||targetUserIds.length===0)return{ok:false,reason:"no_recipients"};
  try{
    const r=await fetch(`${SUPA_URL}/functions/v1/send-email-notification`,{
      method:"POST",
      headers:{...api.h(token),"Content-Type":"application/json"},
      body:JSON.stringify({targetUserIds,category,title,body,url}),
    });
    if(!r.ok){
      let detail="";
      try{detail=await r.text();}catch(e){}
      console.error(`sendEmailNotification failed (${r.status}):`,detail);
      return{ok:false,reason:"http_error",status:r.status};
    }
    return await r.json();
  }catch(e){ console.error("sendEmailNotification failed:",e); return{ok:false,reason:"network_error"}; }
}

export async function getNotificationPrefs(token,userId){
  const rows=await api.get(token,"notification_preferences",`user_id=eq.${userId}`);
  return (rows&&rows[0])||{new_match:true,match_results:true,new_message:true,new_achievement:true,email_enabled:false,email_new_match:true,email_match_results:true,email_new_message:true,email_new_achievement:true};
}

export async function upsertNotificationPref(token,userId,field,value){
  const existing=await api.get(token,"notification_preferences",`user_id=eq.${userId}`);
  if(existing&&existing[0]){
    await api.patchWhere(token,"notification_preferences",`user_id=eq.${userId}`,{[field]:value,updated_at:new Date().toISOString()});
  } else {
    await api.post(token,"notification_preferences",[{user_id:userId,[field]:value}]);
  }
}

export async function getPlayerNotifyRecipients(token,playerId){
  const ids=new Set();
  const playerViewers=await api.get(token,"team_viewers",`player_id=eq.${playerId}&account_role=eq.player&select=id`);
  (playerViewers||[]).forEach(v=>ids.add(v.id));
  const parentLinks=await api.get(token,"parent_children",`player_id=eq.${playerId}&select=parent_viewer_id`);
  (parentLinks||[]).forEach(p=>ids.add(p.parent_viewer_id));
  return [...ids];
}

export async function getTeamNotifyRecipients(token,teamId){
  const ids=new Set();
  const viewers=await api.get(token,"team_viewers",`team_id=eq.${teamId}&select=id`);
  (viewers||[]).forEach(v=>ids.add(v.id));
  return [...ids];
}

export async function getTeamChatRecipients(token,teamId,channel,excludeUserId){
  const ids=new Set();
  const teamRows=await api.get(token,"teams",`id=eq.${teamId}&select=org_id`);
  const orgId=teamRows&&teamRows[0]?teamRows[0].org_id:null;
  if(orgId){
    const ocs=await api.get(token,"org_coaches",`org_id=eq.${orgId}&select=coach_id`);
    (ocs||[]).forEach(o=>ids.add(o.coach_id));
  }
  const role=channel==="parent"?"parent":"player";
  const viewers=await api.get(token,"team_viewers",`team_id=eq.${teamId}&account_role=eq.${role}&select=id`);
  (viewers||[]).forEach(v=>ids.add(v.id));
  ids.delete(excludeUserId);
  return [...ids];
}

export async function getLeagueChatRecipients(token,leagueId,excludeUserId){
  const lts=await api.get(token,"league_teams",`league_id=eq.${leagueId}&status=eq.accepted&left_league_at=is.null&select=org_id`);
  const orgIds=new Set((lts||[]).map(l=>l.org_id).filter(Boolean));
  const league=await api.get(token,"leagues",`id=eq.${leagueId}&select=owner_org_id`);
  if(league&&league[0])orgIds.add(league[0].owner_org_id);
  if(orgIds.size===0)return [];
  const ocs=await api.get(token,"org_coaches",`org_id=in.(${[...orgIds].join(",")})&select=coach_id`);
  const ids=new Set((ocs||[]).map(o=>o.coach_id));
  ids.delete(excludeUserId);
  return [...ids];
}

